import { Formik } from "formik";
import * as React from "react";
import { useCallback, useContext, useEffect, useState } from "react";
import { google_meet_activities_logo, info } from "src/images/NewDesign";
import { onlyDigits } from "src/utils/format";
import { useTabFocus } from "src/utils/hooks";
import { COUNTRY_CODE_REGEX, PHONE_REGEX } from "src/utils/regex";
import { appToast } from "src/utils/toast";
import styled from "styled-components";
import * as Yup from "yup";
import { ModalContext } from "../../context";
import google_meet_dial_in from "../../images/screenshots/google_meet_dial_in.png";
import google_meet_no_extension from "../../images/screenshots/google_meet_no_extension.png";
import google_meet_recording_reminder from "../../images/screenshots/google_meet_recording_reminder.png";
import { theme } from "../../utils/theme";
import { PhoenixInputField } from "../Field/Phoenix";
import { AppText, FlexDiv } from "../UI";
import { PhoenixAppButton, PhoenixIcon } from "../UI/Phoenix";
import { Modal } from "./Modal";

const JoinGoogleMeetModal: React.FC = () => {
  const { showJoinGoogleMeetModal, setShowJoinGoogleMeetModal, joinGoogleMeetModalData } = useContext(ModalContext);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [pin, setPin] = useState("");
  const [extensionExplainerStep, setExtensionExplainerStep] = useState<boolean>(false);
  const [recordingExplainerStep, setRecordingExplainerStep] = useState<boolean>(false);

  const parseClipboardData = (data: string) => {
    const phoneRegex = /MEET-PHONE:(.*?)PIN:/;
    const pinRegex = /PIN:(.*)/;
    const phoneMatch = data.match(phoneRegex);
    const pinMatch = data.match(pinRegex);
    const phone = phoneMatch?.[1]?.trim()?.replace(/\s/g, "")?.replace(/-/g, "");
    const pin = pinMatch?.[1]?.trim()?.replace("#", "")?.replace(/\s/g, "");
    return { phone, pin };
  };

  const clearClipboardData = async () => {
    try {
      await navigator.clipboard.writeText("");
    } catch (err) {
      console.error("Failed to clear clipboard contents: ", err);
    }
  };

  const readClipboardData = useCallback(async () => {
    if (phoneNumber?.length && pin?.length) return;
    try {
      const clipboardData = await navigator.clipboard.readText();
      const clipboardDataIsFromExtension = clipboardData?.includes("MEET-PHONE:") && clipboardData?.includes("PIN:");

      if (clipboardDataIsFromExtension) {
        const { phone, pin } = parseClipboardData(clipboardData);

        phone && setPhoneNumber(phone);
        pin && setPin(pin);
        appToast("Autofilled phone number and pin");
        await clearClipboardData();
      }
    } catch (err) {
      console.error("Failed to read clipboard contents: ", err);
    }
  }, [phoneNumber, pin]);

  const clearAnyExistingClipboardMeetingData = async () => {
    const clipboardData = await navigator.clipboard.readText();
    const clipboardDataIsFromExtension = clipboardData?.includes("MEET-PHONE:") && clipboardData?.includes("PIN:");

    if (clipboardDataIsFromExtension) {
      await clearClipboardData();
    }
  };

  useEffect(() => {
    clearAnyExistingClipboardMeetingData();
  }, []);

  useTabFocus(async () => {
    setTimeout(async () => {
      if (document.hasFocus()) {
        await readClipboardData();
      }
    }, 1000);
  });

  useEffect(() => {
    const interval = setInterval(async () => {
      if (document.hasFocus()) {
        await readClipboardData();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Modal open={showJoinGoogleMeetModal} onClose={() => setShowJoinGoogleMeetModal(false)}>
      <EditCardDiv>
        {extensionExplainerStep ? (
          <NoExtensionStep
            setExtensionExplainerStep={setExtensionExplainerStep}
            joinGoogleMeetModalData={joinGoogleMeetModalData}
          />
        ) : recordingExplainerStep ? (
          <HowToRecordMeetingStep setRecordingExplainerStep={setRecordingExplainerStep} />
        ) : (
          <MainStep
            phoneNumber={phoneNumber}
            pin={pin}
            setPhoneNumber={setPhoneNumber}
            setPin={setPin}
            setRecordingExplainerStep={setRecordingExplainerStep}
            setExtensionExplainerStep={setExtensionExplainerStep}
            setShowJoinGoogleMeetModal={setShowJoinGoogleMeetModal}
            joinGoogleMeetModalData={joinGoogleMeetModalData}
          />
        )}
      </EditCardDiv>
    </Modal>
  );
};

const MainStep = ({
  phoneNumber,
  pin,
  setPhoneNumber,
  setPin,
  setExtensionExplainerStep,
  setRecordingExplainerStep,
  setShowJoinGoogleMeetModal,
  joinGoogleMeetModalData,
}: {
  phoneNumber: string;
  pin: string;
  setPhoneNumber: (phoneNumber: string) => void;
  setPin: (pin: string) => void;
  setExtensionExplainerStep: (extensionExplainerStep: boolean) => void;
  setRecordingExplainerStep: (recordingExplainerStep: boolean) => void;
  setShowJoinGoogleMeetModal: (showJoinGoogleMeetModal: boolean) => void;
  joinGoogleMeetModalData: {
    dialInToMeeting: ({ newPhoneNumber, newPin }: { newPhoneNumber: string; newPin: string }) => void;
    meetingLink: string;
  };
}) => {
  const [hasLaunchedJoinGoogleMeetWindow, setHasLaunchedJoinGoogleMeetWindow] = useState<boolean>(false);

  const SELLFIRE_EXTENSION_LINK =
    "https://chromewebstore.google.com/detail/ljfjngncicimgjjcjigboilfocmlcajg?utm_source=item-share-cb";

  return (
    <Formik
      enableReinitialize
      initialValues={{
        phoneNumber: phoneNumber,
        pin: pin,
      }}
      validationSchema={Yup.object({
        phoneNumber: Yup.string()
          .required("Field is required")
          .max(15, "Must be valid phone number")
          .min(7, "Must be valid phone number")
          .matches(PHONE_REGEX, "Must be a valid phone number")
          .matches(COUNTRY_CODE_REGEX, "Must include the country code."),

        pin: Yup.string().required("PIN is required"),
      })}
      onSubmit={(values) => {
        joinGoogleMeetModalData?.dialInToMeeting({
          newPhoneNumber: `+${onlyDigits(values.phoneNumber)}`,
          newPin: values.pin,
        });
        setShowJoinGoogleMeetModal(false);
      }}
    >
      {({ handleSubmit, handleChange, values, errors, touched, isValid }) => (
        <>
          <TitleDiv>
            <TitleText>Join a Google Meet Meeting</TitleText>
          </TitleDiv>
          <Main>
            <>
              <img src={google_meet_dial_in} alt="Google Meet Dial In" height="100%" width="100%" />
              <FlexDiv direction="column" gap={8}>
                <AppText fontSize={14} fontWeight={500}>
                  Fill in the generated phone number and PIN.
                </AppText>
                <InstructionsList>
                  <ListItem>
                    Make sure you've installed the{" "}
                    <Link onClick={() => window.open(SELLFIRE_EXTENSION_LINK, "_blank")}>
                      Sellfire Extension for Chrome.
                    </Link>
                  </ListItem>
                  <ListItem>
                    Click the <Bold>Launch Window</Bold> button.
                  </ListItem>
                  <ListItem>
                    Select <Bold>"Join and use a phone for audio."</Bold>
                  </ListItem>
                  <ListItem>Come back to this tab to auto-fill the number and PIN.</ListItem>
                </InstructionsList>
              </FlexDiv>
              <PhoenixInputField name="phoneNumber" titleText="Phone Number" requiredStar titleTextSpacing={8} />
              <PhoenixInputField name="pin" requiredStar titleText="Pin" titleTextSpacing={8} />
              <FlexDiv direction="column" gap={0}>
                <PhoenixAppButton
                  variant="brand-outline"
                  buttonType="ghost-small"
                  onClick={() => setExtensionExplainerStep(true)}
                >
                  Haven't installed the Sellfire Extension yet?
                </PhoenixAppButton>
                <PhoenixAppButton
                  variant="brand-outline"
                  buttonType="ghost-small"
                  onClick={() => setRecordingExplainerStep(true)}
                >
                  How to record a Google Meet meeting?
                </PhoenixAppButton>
              </FlexDiv>
            </>
          </Main>
          <SubmitDiv>
            <FlexDiv gap={8} justify="space-between" width="100%">
              <PhoenixAppButton
                buttonType="secondary"
                variant="brand-outline"
                onClick={() => {
                  setHasLaunchedJoinGoogleMeetWindow(true);
                  window.open(joinGoogleMeetModalData?.meetingLink, "_blank");
                }}
              >
                Launch Window
              </PhoenixAppButton>

              <PhoenixAppButton
                disabled={!isValid}
                buttonType="secondary"
                variant="brand"
                onClick={() => {
                  handleSubmit();
                }}
              >
                Join Meeting
              </PhoenixAppButton>
            </FlexDiv>
            <FlexDiv gap={8} align="center">
              <PhoenixIcon svg={info} variant="danger" size={16} />
              <AppText fontSize={12} fontWeight={400} color={theme.text.danger.primary}>
                Record this meeting for future reference.
              </AppText>
            </FlexDiv>
          </SubmitDiv>
        </>
      )}
    </Formik>
  );
};

const NoExtensionStep = ({
  setExtensionExplainerStep,
  joinGoogleMeetModalData,
}: {
  setExtensionExplainerStep: (extensionExplainerStep: boolean) => void;
  joinGoogleMeetModalData: {
    meetingLink: string;
  };
}) => {
  const SELLFIRE_EXTENSION_LINK = "https://chromewebstore.google.com/detail/sellfire/ljfjngncicimgjjcjigboilfocmlcajg";

  return (
    <>
      <TitleDiv>
        <TitleText>Haven't installed the Sellfire Extension yet?</TitleText>
      </TitleDiv>
      <Main>
        <img src={google_meet_no_extension} alt="Google Meet No Extension" height="100%" width="100%" />
        <FlexDiv direction="column" gap={8}>
          <AppText fontSize={12} fontWeight={400}>
            No worries! You can install it{" "}
            <Link onClick={() => window.open(SELLFIRE_EXTENSION_LINK, "_blank")}>here</Link> or enter the info manually.
          </AppText>
          <AppText fontSize={12} fontWeight={400}>
            To find your dial in number and PIN:
          </AppText>
          <InstructionsList>
            <ListItem>
              Go to{" "}
              <Link onClick={() => window.open(joinGoogleMeetModalData?.meetingLink, "_blank")}>Google Meet.</Link>
            </ListItem>
            <ListItem>
              Select <Bold>"Join and use a phone for audio."</Bold>
            </ListItem>
            <ListItem>
              Open the <Bold>"Dial in using phone audio"</Bold> tab to find your phone number and PIN.
            </ListItem>
          </InstructionsList>
          <AppText fontSize={10} fontWeight={400} color={theme.text.neutral.tertiary}>
            Each meeting generates unique numbers. Do not copy the above.
          </AppText>
        </FlexDiv>
      </Main>
      <SubmitDiv>
        <PhoenixAppButton buttonType="secondary" variant="brand" onClick={() => setExtensionExplainerStep(false)}>
          Got it
        </PhoenixAppButton>
      </SubmitDiv>
    </>
  );
};

const HowToRecordMeetingStep = ({
  setRecordingExplainerStep,
}: {
  setRecordingExplainerStep: (recordingExplainerStep: boolean) => void;
}) => {
  return (
    <>
      <TitleDiv>
        <TitleText>How to record a Google Meet meeting?</TitleText>
      </TitleDiv>
      <Main>
        <>
          <img src={google_meet_recording_reminder} alt="Google Meet Recording Reminder" height="100%" width="100%" />
          <InstructionsList>
            <ListItem>
              At the bottom right, click <Bold>"Activities"</Bold>{" "}
              <img src={google_meet_activities_logo} alt="Google Meet Activities" height="12px" width="12px" />
            </ListItem>
            <ListItem>
              Click <Bold>"Recording"</Bold>, then click <Bold>"Start Recording."</Bold>
            </ListItem>
            <ListItem>You will hear an indication when the recording starts.</ListItem>
          </InstructionsList>
        </>
      </Main>
      <SubmitDiv>
        <PhoenixAppButton buttonType="secondary" variant="brand" onClick={() => setRecordingExplainerStep(false)}>
          Got it
        </PhoenixAppButton>
      </SubmitDiv>
    </>
  );
};

const InstructionsList = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 20px;
`;

const ListItem = styled.li`
  font-weight: 400;
  font-size: 12px;
  padding-left: 2px;
`;

const Link = styled.span`
  color: ${theme.text.brand.primary};
  cursor: pointer;
  font-weight: 600;
  font-size: 12px;
`;

const Bold = styled.span`
  font-weight: bold;
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  width: 100%;
  padding: 0px 40px 0px 40px;
`;

const TitleText = styled(AppText)`
  font-weight: 600;
  font-size: 16px;
`;

const TitleDiv = styled.div`
  position: relative;
  min-height: 56px;
  max-height: 56px;
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const EditCardDiv = styled.div`
  z-index: 5;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 460px;
  max-width: 70vw;
  gap: 24px;
  max-height: 80vh;

  overflow: auto;
`;

const SubmitDiv = styled.div`
  display: flex;
  border-top: 1px solid ${theme.border.neutral.secondary};
  align-items: center;
  padding: 16px 40px;
  flex-direction: column;
  gap: 12px;
  height: max-content;
`;

export { JoinGoogleMeetModal };
